/**
 * The default directory the favicon PNGs are served from.
 *
 * This is the build-time default and must keep reproducing the original
 * hardcoded `/img/favicon_*.png` paths, because `web-frontend/Dockerfile`
 * builds the Nuxt app at *image build* time while the per-deployment favicon
 * path is only known at *container run* time. Deployments override it at
 * runtime with `BASEROW_FAVICON_PATH` (see `plugins/favicon.js`).
 */
export const DEFAULT_FAVICON_PATH = '/img'

/**
 * The favicon `<link>` descriptors, without an `href`.
 *
 * `size` is the pixel size used to build the filename (`favicon_<size>.png`)
 * and `sizes`/`key` are the `<link>` attributes. The `key` is what allows other
 * parts of the app (e.g. the application builder's custom favicon) to override
 * these defaults, because unhead dedupes `link` tags by `key`.
 *
 * Note `favicon_48.png` is deliberately advertised as `64x64` with the
 * `favicon-64` key; that mismatch predates the runtime path support and is kept
 * as-is so existing behaviour and override keys do not change.
 */
/**
 * The default product name used in the browser tab title.
 *
 * Like `DEFAULT_FAVICON_PATH`, this is the build-time default and must keep
 * reproducing the original hardcoded `Baserow` / `%s | Baserow` values, because
 * the Nuxt app is built once into an image shared by several deployments while
 * the per-deployment brand name is only known at container run time.
 * Deployments override it at runtime with `BASEROW_BRAND_NAME` (see
 * `plugins/brandName.js`).
 */
export const DEFAULT_BRAND_NAME = 'Baserow'

/**
 * Builds the tab `title` / `titleTemplate` pair for a given brand name.
 *
 * @param {string} brandName the product name shown in the browser tab.
 * @returns {{title: string, titleTemplate: string}} the head title fields.
 */
export const buildTitle = (brandName = DEFAULT_BRAND_NAME) => ({
  title: brandName,
  titleTemplate: `%s | ${brandName}`,
})

/**
 * Translates a brand-facing string that embeds the product name.
 *
 * This is the frontend twin of `branded_subject()` in `backend/src/baserow/
 * core/emails.py`, and it exists for the same reason. The stock English copy
 * ("New to Baserow?") is translated in the 20 Weblate-managed catalogs next to
 * `locales/en.json`, keyed on the *key* rather than the English string. Adding
 * a `{brandName}` placeholder to the English value alone is therefore safe for
 * the catalogs, but it is not enough on its own:
 *
 * - vue-i18n renders a missing named param as an empty string, so a `{brandName}`
 *   value reached without params produces broken copy ("New to ?"). Keeping the
 *   original key's value literal means that failure mode cannot happen.
 * - a locale that already translates the key keeps its own literal "Baserow",
 *   so an unbranded deployment must not depend on interpolation either.
 *
 * So each converted string keeps its original key with the stock value intact
 * and gains a sibling `<key>Branded` key holding the `{brandName}` form. This
 * helper picks the branded key only for a deployment that actually renamed
 * itself, which makes an unbranded deployment byte-identical in *every*
 * language, and a branded one correct in English (plus any language whose
 * catalog later picks up the branded key).
 *
 * The first argument is the *translate function itself*, not an i18n instance,
 * because "the i18n object" is not one shape in Nuxt 3 + vue-i18n. vue-i18n
 * installs two distinct globals (see `injectGlobalFields`): the translate
 * methods land on `$t`/`$d`/`$n`, while `$i18n` is a separate, null-prototype
 * object carrying only `locale`, `fallbackLocale` and `availableLocales`. So a
 * component instance's `this.$i18n` has **no** `t`, whereas the Nuxt app's
 * `nuxtApp.$i18n` is the Composer (`i18n.global`) and does. Taking a plain
 * function keeps one unambiguous contract and pushes the "which shape is this?"
 * decision to the call site, which is the only place that knows.
 *
 * @param {function} t the translate function, e.g. a component's `$t`, or
 *   `nuxtApp.$i18n.t` bound to its Composer.
 * @param {string} brandName the configured product name.
 * @param {string} key the original, unbranded i18n key.
 * @param {object} values any other interpolation values the key needs.
 * @returns {string} the translated copy.
 */
export const brandedMessage = (t, brandName, key, values = {}) => {
  if (!brandName || brandName === DEFAULT_BRAND_NAME) {
    return t(key, values)
  }

  return t(`${key}Branded`, { ...values, brandName })
}

export const FAVICONS = [
  { size: 16, sizes: '16x16', key: 'favicon-16' },
  { size: 32, sizes: '32x32', key: 'favicon-32' },
  { size: 48, sizes: '64x64', key: 'favicon-64' },
  { size: 192, sizes: '192x192', key: 'favicon-192' },
]

/**
 * Builds the favicon `<link>` tags for a given base path.
 *
 * @param {string} path the directory the favicon PNGs are served from, without
 *   a trailing slash.
 * @returns {object[]} the favicon `<link>` tags.
 */
export const buildFaviconLinks = (path = DEFAULT_FAVICON_PATH) =>
  FAVICONS.map(({ size, sizes, key }) => ({
    rel: 'icon',
    type: 'image/png',
    href: `${path}/favicon_${size}.png`,
    sizes,
    key,
  }))

export default {
  ...buildTitle(),
  meta: [
    { charset: 'utf-8' },
    {
      name: 'viewport',
      content:
        'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no',
    },
    {
      name: 'format-detection',
      content: 'telephone=no, email=no, address=no, date=no',
    },
  ],
  link: buildFaviconLinks(),
}
